import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { isEnum } from 'utils/isEnum';
import { kebabToCamelCase } from 'utils/kebabToCamelCase';

import Error404Page from 'pages/Error404Page';

type ContextType<T> = T;

export const ParamContext = createContext<ContextType<any>>('');

export const useParamContext = <T,>() => {
  return useContext<ContextType<T>>(ParamContext);
};

interface ParamContextProviderProps<T> {
  children: ReactNode;
  paramName: string;
  enumName: Record<string, T>;
  fallback?: React.ReactChild;
}

export function ParamContextProvider<T extends string>({
  children,
  paramName,
  enumName,
  fallback = <Error404Page />,
}: ParamContextProviderProps<T>) {
  const params = useParams();
  const paramValue = kebabToCamelCase(params[paramName] || '');

  if (!isEnum(paramValue, enumName)) {
    return <>{fallback}</>;
  }

  return <ParamContext.Provider value={paramValue}>{children}</ParamContext.Provider>;
}
